import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { extractRfq, type ExtractionResponse } from '@/api/rfqs';
import type { ExtractedField, FieldConfidence } from '@/types/generated';

/**
 * Paste the customer's request, see what was read, then fill the form.
 *
 * THE REVIEW STEP IS THE FEATURE, not a formality on the way to the form. A parser that
 * silently populated twenty fields would be faster and untrustworthy: the salesperson
 * would have no way to tell a value the customer stated from one the parser guessed, and
 * the first time a guess was wrong they would stop believing the other nineteen.
 *
 * So every field arrives with three things — what was read, WHY, and the fragment it came
 * from — and the ones that need a human sort to the top. Checking a parse against the
 * original becomes a five-second scan down the right-hand column instead of a re-read of
 * the email.
 *
 * WHAT IT CANNOT DO IS ON THE PANEL, IN THE PANEL'S OWN WORDS. The footer says this is a
 * rule-based parser rather than a language model, and quotes the measured figure with its
 * sample size. Putting it in the interface rather than only in a document means nobody can
 * demonstrate this feature while describing it as something it is not.
 */

interface Props {
  /** Merge the parsed values into the wizard's form state. */
  onApply: (values: Record<string, string | number | boolean>) => void;
}

const PRESENTATION: Record<
  FieldConfidence,
  { chip: string; colour: 'success' | 'info' | 'warning' | 'error'; icon: typeof CheckCircleOutlineIcon }
> = {
  stated: { chip: 'Stated', colour: 'success', icon: CheckCircleOutlineIcon },
  inferred: { chip: 'From context', colour: 'info', icon: HelpOutlineIcon },
  guessed: { chip: 'Check this', colour: 'warning', icon: WarningAmberIcon },
  not_found: { chip: 'Not identified', colour: 'error', icon: ErrorOutlineIcon },
};

const PLACEHOLDER = `Paste the customer's email, WhatsApp message or tender extract here.

For example:

Customer: Bidco Africa Ltd
Collection: Mombasa Port, Kilindini
Delivery: Kampala, Uganda
Gross weight: 18,500 kg
Cargo ready date: 12 August 2026`;

export function RfqPastePanel({ onApply }: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [applied, setApplied] = useState(false);

  const extraction = useMutation({
    mutationFn: extractRfq,
    // A second paste replaces the first reading rather than merging with it — two
    // documents' fields interleaved would be impossible to review.
    onSuccess: () => setApplied(false),
  });

  const result: ExtractionResponse | undefined = extraction.data;

  const apply = () => {
    if (!result) return;
    onApply(result.values);
    setApplied(true);
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: open ? 2 : 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ContentPasteIcon fontSize="small" color="action" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">Start from the customer's message</Typography>
            <Typography variant="caption" color="text.secondary">
              Paste an email, WhatsApp message or tender extract and we will fill in what we can read.
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setOpen((current) => !current)}
            endIcon={open ? <ExpandLessIcon /> : undefined}
          >
            {open ? 'Hide' : 'Paste a request'}
          </Button>
        </Stack>

        <Collapse in={open} unmountOnExit>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              multiline
              minRows={6}
              maxRows={16}
              fullWidth
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={PLACEHOLDER}
              slotProps={{ htmlInput: { 'aria-label': 'Customer request text' } }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                onClick={() => extraction.mutate(text)}
                disabled={text.trim().length < 10 || extraction.isPending}
              >
                {extraction.isPending ? 'Reading…' : 'Read this request'}
              </Button>
              {text !== '' && (
                <Button
                  size="small"
                  onClick={() => {
                    setText('');
                    extraction.reset();
                    setApplied(false);
                  }}
                >
                  Clear
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {/* Nothing is written until the form is submitted, and saying so is what
                  makes people willing to paste again. */}
              <Typography variant="caption" color="text.secondary">
                Nothing is saved until you submit the form.
              </Typography>
            </Stack>

            {extraction.isError && (
              <Alert severity="error">
                Could not read that text. The request can still be filled in by hand below.
              </Alert>
            )}

            {result && <Reading result={result} applied={applied} onApply={apply} />}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function Reading({
  result,
  applied,
  onApply,
}: {
  result: ExtractionResponse;
  applied: boolean;
  onApply: () => void;
}) {
  // Worst first. `needs_review` is already ordered by the server; the stated fields follow,
  // because a reviewer scanning for problems should meet them before the good news.
  const ordered = [
    ...result.needs_review,
    ...result.fields.filter((field) => field.confidence === 'stated'),
  ];

  const missing = result.not_found.length;

  return (
    <Stack spacing={1.5}>
      <Divider />

      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">{result.summary}</Typography>
        {missing > 0 && (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={`${missing} you will need to fill in`}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant={applied ? 'outlined' : 'contained'} size="small" onClick={onApply}>
          {applied ? 'Applied — apply again' : 'Fill in the form'}
        </Button>
      </Stack>

      {applied && (
        <Alert severity="success" sx={{ py: 0 }}>
          Filled in below. Check anything marked <strong>Check this</strong> or{' '}
          <strong>Not identified</strong> before continuing.
        </Alert>
      )}

      <Box
        sx={{
          maxHeight: 340,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {ordered.map((field) => (
          <FieldRow key={field.field} field={field} />
        ))}
      </Box>

      {/*
        THE HONEST FOOTER. This is a deterministic parser and the panel says so, with the
        measured figure and the size of the sample it was measured on. A held-out set is
        quoted rather than the tuned one, because the tuned figure is 100% and means
        nothing — it only shows the parser was fixed until it agreed with its own examples.
      */}
      <Typography variant="caption" color="text.secondary">
        Read by a rule-based parser — pattern matching, unit conversion and a place-name list
        for this region. Not a language model, and nothing is sent outside this system.
        Measured 89.9% field accuracy on 10 held-out sample requests it was not built
        against; 97.6% of the fields it fills are right.
      </Typography>
    </Stack>
  );
}

function FieldRow({ field }: { field: ExtractedField }) {
  const { chip, colour, icon: Icon } = PRESENTATION[field.confidence];

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Icon fontSize="small" color={colour} sx={{ mt: 0.25 }} />

      <Box sx={{ minWidth: 132 }}>
        <Typography variant="body2" fontWeight={500}>
          {field.label}
        </Typography>
        {field.reason && (
          <Typography variant="caption" color="text.secondary" display="block">
            {field.reason}
          </Typography>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {display(field)}
        </Typography>
        {field.evidence && (
          // The fragment it was read from. This is what makes a wrong reading catchable by
          // eye instead of by re-reading the whole email.
          <Tooltip title="The part of the request this came from">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', display: 'block', wordBreak: 'break-word' }}
            >
              “{field.evidence}”
            </Typography>
          </Tooltip>
        )}
      </Box>

      <Chip size="small" label={chip} color={colour} variant="outlined" />
    </Stack>
  );
}

function display(field: ExtractedField): string {
  if (field.value === null || field.value === '') return '—';
  if (typeof field.value === 'boolean') return field.value ? 'Yes' : 'No';

  const text = String(field.value);

  // The notes field carries the whole original request; showing it in full would push
  // everything else off the panel.
  return text.length > 160 ? `${text.slice(0, 157)}…` : text;
}
